import { Box, useTheme } from "@mui/material";
import { QueryClientProvider, useIsFetching } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useContext, useEffect, useState } from "react";
import { AntiForgeryConfig } from "../helpers";
import { queryClient } from "./queryClient";
export { useQuery } from "@tanstack/react-query";

interface TanStackProviderProps {
  staleTime?: number;
  children?: React.ReactNode;
  someQueryIsFetching?: (isLoading: boolean) => void;
  antiForgeryToken?: AntiForgeryConfig;
}
type ITanStackProviderSettings = Pick<TanStackProviderProps, "staleTime" | "antiForgeryToken">;

const TanStackProviderSettings = React.createContext<ITanStackProviderSettings>({});

export const useTanStackSettings = () => useContext(TanStackProviderSettings);

export const TanStackProvider: React.FC<TanStackProviderProps> = ({
  staleTime,
  children,
  someQueryIsFetching,
  antiForgeryToken,
}) => {
  const isDevMode = import.meta.env.DEV;

  return (
    <TanStackProviderSettings.Provider
      value={{
        staleTime,
        antiForgeryToken,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <TanStackLoaderEffect someQueryIsFetching={someQueryIsFetching} />
        {isDevMode && <TanStackQueryDevtools />}
        {children}
      </QueryClientProvider>
    </TanStackProviderSettings.Provider>
  );
};

const TanStackLoaderEffect: React.FC<{
  someQueryIsFetching?: (isLoading: boolean) => void;
}> = ({ someQueryIsFetching }) => {
  const isFetching = useIsFetching() > 0;
  useEffect(() => {
    if (someQueryIsFetching) {
      someQueryIsFetching(isFetching);
    }
  }, [isFetching]);
  return null;
};

const TanStackQueryDevtools = () => {
  const { pxToRem } = useTheme().typography;

  const [isServerSide, setIsServerSide] = useState(true);
  useEffect(() => setIsServerSide(false), []);
  if (isServerSide) return;

  return (
    <Box
      sx={{
        display: "block",
        position: "fixed",
        bottom: `calc(50vh - ${pxToRem(24)})`,
        left: pxToRem(2),
        zIndex: 10,
        cursor: "default",
        textAlign: "center",
        ["button.tsqd-open-btn"]: {
          width: pxToRem(16),
          height: pxToRem(16),
        },
      }}
    >
      <ReactQueryDevtools buttonPosition="relative" />
    </Box>
  );
};

/**
 * To trigger a refetch
 *
 * TODO Go for useMutate as soon as ready for it https://tanstack.com/query/latest/docs/framework/react/guides/mutations
 *
 * @param path
 */
export const invalidateQueryTanStack = (path: string) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  queryClient.invalidateQueries({ queryKey: [path] });
};

export const removeQueryTanStack = (path: string) => {
  queryClient.removeQueries({ queryKey: [path] });
};

export const setQueryDataTanStack = <T,>(path: string, data: T) => {
  queryClient.setQueryData<T>([path], data);
};
